import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Link2, RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Normalise whatever App A returns from /available-assessments into an array.
 * Known shapes: [{...}], {assessments: [...]}, {items: [...]}, {results: [...]}.
 */
function normaliseList(data) {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    return data.assessments || data.items || data.results || [];
  }
  return [];
}

export default function ImportAssessmentModal({ open, onOpenChange, onImported }) {
  const [loading, setLoading] = useState(false);
  const [conn, setConn] = useState(null);
  const [items, setItems] = useState([]);
  const [manualId, setManualId] = useState("");

  const testConn = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sync/test-connection");
      setConn(data);
      if (data.status === "ok") {
        toast.success(
          `Connected · ${data.latency_ms ?? "—"}ms · schema ${data.schema_version || "?"}`
        );
      } else {
        toast.error(`App A: ${data.error || "unreachable"}`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error(`Test failed: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const loadList = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sync/available-assessments");
      const list = normaliseList(data);
      setItems(list);
      if (!list.length) toast("No assessments returned from App A yet.");
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error(`List failed: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const doImport = async (assessment_id) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/sync/import/${assessment_id}`);
      toast.success(
        `Imported ${assessment_id} · ${data.controls_seeded ?? 0} controls · ${data.findings_seeded ?? 0} findings`
      );
      onImported?.(data);
      onOpenChange(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error(`Import failed: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) { setItems([]); setConn(null); testConn(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="import-assessment-modal">
        <DialogHeader>
          <DialogTitle className="text-[#003366]">Import Assessment from App A</DialogTitle>
          <DialogDescription>
            Pulls the V2.2 GRC payload from <span className="font-mono">dpdp.codexero.ai</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Button
              variant="secondary" onClick={testConn} disabled={loading}
              className="grc-btn" data-testid="test-connection-button"
            >
              <Link2 className="w-4 h-4 mr-2" /> Test Connection
            </Button>
            <Button
              variant="secondary" onClick={loadList} disabled={loading}
              className="grc-btn" data-testid="list-assessments-button"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> List Assessments
            </Button>
            {loading && <Loader2 className="w-4 h-4 animate-spin text-[#008080]" />}
          </div>

          {conn && (
            <div className="text-xs font-mono p-3 rounded-[8px] border border-slate-200 bg-slate-50 space-y-1" data-testid="test-connection-result">
              <div className={conn.status === "ok" ? "text-emerald-700" : "text-red-700"}>
                {conn.status === "ok"
                  ? `✔ OK · ${conn.latency_ms ?? "—"}ms · schema ${conn.schema_version || "?"} · ${conn.assessments_available ?? "?"} assessments available`
                  : `✖ Not OK${conn.http_status ? ` · HTTP ${conn.http_status}` : ""}`}
              </div>
              <div className="text-slate-600 break-all">{conn.dpdp_url}</div>
              {conn.error && (
                <div className="text-red-700 break-all" data-testid="test-connection-error">{conn.error}</div>
              )}
              {(conn.http_status === 401 || conn.http_status === 403) && (
                <div className="mt-2 p-2 rounded border border-amber-200 bg-amber-50 text-amber-900" data-testid="test-connection-hint">
                  Hint: set <span className="font-semibold">DPDP_API_KEY</span> in <span className="font-semibold">backend/.env</span> to the rotated GRC integration key from App A, then restart the backend.
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">Available Assessments</div>
            <div className="border border-slate-200 rounded-[8px] max-h-56 overflow-auto">
              {items.length === 0 ? (
                <div className="p-4 text-sm text-slate-500">None loaded. Click "List Assessments" above.</div>
              ) : items.map((a) => {
                const id = a.assessment_id || a.id;
                const label = a.org_name || a.organisation || a.name || id;
                return (
                  <div key={id} className="flex items-center justify-between px-3 py-2 border-b last:border-b-0 border-slate-100">
                    <div className="text-sm">
                      <div className="font-medium text-[#003366]">{label}</div>
                      <div className="font-mono text-xs text-slate-500">
                        {id} {a.submitted_at ? `· ${a.submitted_at.slice(0, 10)}` : ""}
                      </div>
                    </div>
                    <Button
                      size="sm" className="grc-btn bg-[#008080] hover:bg-[#008080]/90"
                      onClick={() => doImport(id)}
                      data-testid={`import-btn-${id}`}
                    >
                      <Download className="w-4 h-4 mr-1" /> Import
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="pt-2 border-t border-slate-100">
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Or import by ID</div>
            <div className="flex gap-2">
              <Input
                placeholder="assessment_id (uuid from App A)"
                value={manualId}
                onChange={(e) => setManualId(e.target.value)}
                data-testid="manual-assessment-id-input"
              />
              <Button
                onClick={() => manualId && doImport(manualId)}
                disabled={!manualId || loading}
                className="grc-btn bg-[#003366] hover:bg-[#003366]/90"
                data-testid="manual-import-button"
              >
                Import
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="close-import-modal">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
